import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import * as endpoints from "../networkUtility/endpoints";
import * as localStorageActiontype from "../LocalStorage/ActionTypes";
import { getLocalStorage } from "../LocalStorage/GetLocalStorage";
import { toggleNotificationVisibility } from "./networkNotification";
import { NotificationType } from "../Utility/InterFacesAndEnum";
import { handle401Status } from "../Utility/Utility";
import { toggleLoader } from "./loader";

interface DashboardData {
  totalUsers: number;
  goldUsers: number;
  regularUsers: number;
  totalLotteries: number;
  upcomingLotteres: number;
  currentLotteries: number;
  executedLotteries: number;
  deletedLotteries: number;
  executedLotteriesTotalRewardPrice: number;
  executedLotteriesTotalTicketsPrice: number;
  executedLotteriesTotalTicketsSold: number;
  executedLotteriesPlatinumTicketsPrice: number;
  executedLotteriesPlatinumTicketsSold: number;
  executedLotteriesGoldTicketsPrice: number;
  executedLotteriesGoldTicketsSold: number;
  executedLotteriesSilverTicketsPrice: number;
  executedLotteriesSilverTicketsSold: number;
  executedLotteriesBronzeTicketsPrice: number;
  executedLotteriesBronzeTicketsSold: number;
  currentLotteriesTotalRewardPrice: number;
  currentLotteriesTotalTicketsPrice: number;
  currentLotteriesTotalTicketsSold: number;
  currentLotteriesPlatinumTicketsPrice: number;
  currentLotteriesPlatinumTicketsSold: number;
  currentLotteriesGoldTicketsPrice: number;
  currentLotteriesGoldTicketsSold: number;
  currentLotteriesSilverTicketsPrice: number;
  currentLotteriesSilverTicketsSold: number;
  currentLotteriesBronzeTicketsPrice: number;
  currentLotteriesBronzeTicketsSold: number;
}

interface DashboardInitialState {
  data: DashboardData | {};
}

const dashboardInitialState: DashboardInitialState = {
  data: {},
};

export const getDashboardData = createAsyncThunk(
  "get dashboard data",
  async (payload, { dispatch }) => {
    dispatch(
      toggleLoader({
        isLoading: true,
      })
    );
    await fetch(endpoints.getDashboardData, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getLocalStorage(
          localStorageActiontype.GET_ACCESS_TOKEN
        )}`,
        "Content-type": "application/json; charset=UTF-8",
      },
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        if (response.status === 401) {
          handle401Status();
        }

        if (response.statusCode === 200) {
          dispatch(
            setDashboardData({
              data: response.result,
            })
          );
          dispatch(
            toggleNotificationVisibility({
              isVisible: true,
              status: NotificationType.success,
              message: response.errorMsg,
            })
          );
        } else {
          dispatch(
            toggleNotificationVisibility({
              isVisible: true,
              status: NotificationType.error,
              message: response.errorMsg,
            })
          );
        }
      })
      .catch((error) => {
        console.log(error, "dashboard error");
      })
      .finally(() => {
        dispatch(
          toggleLoader({
            isLoading: false,
          })
        );
      });
  }
);

export const exportDashboardData = createAsyncThunk(
  "download dashboard data",
  async (payload, { dispatch }) => {
    dispatch(
      toggleLoader({
        isLoading: true,
      })
    );
    await fetch(endpoints.exportDashboardData, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getLocalStorage(
          localStorageActiontype.GET_ACCESS_TOKEN
        )}`,
        "Content-type": "application/json; charset=UTF-8",
      },
    })
      .then(async (response) => {
        dispatch(toggleLoader({ isLoading: false }));

        // Check if the response status is OK (200)
        if (response.status === 200) {
          // Get the filename from the 'Content-Disposition' header
          const contentDisposition = response.headers.get(
            "Content-Disposition"
          );
          const match = contentDisposition?.match(/filename="(.+)"/);
          const fileName = match ? match[1] : "downloaded-file.xlsx";

          // Convert the response to a Blob
          const blob = await response.blob();

          // Create a URL for the Blob
          const url = window.URL.createObjectURL(blob);

          // Create an anchor element to trigger the download
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();

          // Clean up the URL and remove the anchor element
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          dispatch(
            toggleNotificationVisibility({
              isVisible: true,
              status: NotificationType.success,
              message: "File downloaded successfully.",
            })
          );
        } else {
          console.error("Failed to download the file.");
          dispatch(
            toggleNotificationVisibility({
              isVisible: true,
              status: NotificationType.error,
              message: "Failed to download the file.",
            })
          );
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }
);

const dashboardSlice = createSlice({
  name: "Dashboard Slice",
  initialState: dashboardInitialState,
  reducers: {
    setDashboardData: (
      state,
      action: PayloadAction<{ data: DashboardData }>
    ) => {
      return {
        ...state,
        data: action.payload.data,
      };
    },
  },
});

export const { setDashboardData } = dashboardSlice.actions;
export default dashboardSlice.reducer;
